import { computed, inject, Injectable, signal } from '@angular/core';
import { BillingApiService } from './api/billing-api.service';
import { BillingEntitlements, OrgLicense } from '@shared/models';

/**
 * Global reactive store for billing entitlements.
 *
 * Fetched once on app startup (for korrektor users) and refreshed
 * after Stripe checkout returns or when credits are consumed.
 */
@Injectable({
    providedIn: 'root'
})
export class BillingStateService {
    private readonly billingApi = inject(BillingApiService);

    private readonly _entitlements = signal<BillingEntitlements | null>(null);
    private readonly _loading = signal(false);
    private readonly _error = signal<string | null>(null);
    private _loaded = false; // one-shot guard: don't re-fetch unless explicitly refreshed

    /** Current entitlements snapshot (null until first load) */
    readonly entitlements = this._entitlements.asReadonly();

    /** Whether the entitlements are currently being fetched */
    readonly loading = this._loading.asReadonly();

    /** Last load error, if any */
    readonly error = this._error.asReadonly();

    /** Number of available submission credits */
    readonly submissionCredits = computed(() => this._entitlements()?.credits?.submission ?? 0);

    /** Remaining gifted submission credits (welcome gift / reactivation campaign) */
    readonly freeSubmissionCredits = computed(() => this._entitlements()?.freeCredits ?? 0);

    /** Number of available case creation credits */
    readonly caseCreationCredits = computed(() => this._entitlements()?.credits?.custom_case_package ?? 0);

    /** Whether the user has an active Plus subscription */
    readonly isPlusActive = computed(() => this._entitlements()?.plan?.active ?? false);

    /** Whether the user can submit (has at least 1 submission credit) */
    readonly canSubmit = computed(() => this.submissionCredits() > 0);

    /** Whether addon purchases are allowed */
    readonly canPurchaseAddons = computed(() => this._entitlements()?.canPurchaseAddons ?? false);

    /** Whether the user has an active org-level license */
    readonly hasOrgLicense = computed(() => this._entitlements()?.orgLicense?.active ?? false);

    /** The org license info, if present */
    readonly orgLicense = computed(() => this._entitlements()?.orgLicense ?? null);

    /**
     * Fetch entitlements from the backend and update the signal.
     * Safe to call multiple times; concurrent calls are deduplicated.
     */
    async load(): Promise<void> {
        if (this._loaded || this._loading()) return; // skip if already loaded or in-flight

        this._loading.set(true);
        this._error.set(null);

        try {
            const raw = await this.billingApi.getEntitlements();
            this._entitlements.set(this.normalize(raw));
            this._loaded = true;
        } catch (err) {
            console.error('[BillingState] Failed to load entitlements:', err);
            this._error.set('Billing-Daten konnten nicht geladen werden.');
        } finally {
            this._loading.set(false);
        }
    }

    /** Force re-fetch — used after checkout, org-join, credit consumption */
    async refresh(): Promise<void> {
        this._loaded = false;
        return this.load();
    }

    /** Clear state (e.g. on logout) */
    clear(): void {
        this._entitlements.set(null);
        this._error.set(null);
        this._loaded = false;
    }

    private normalize(raw: any): BillingEntitlements {
        const orgLicenseRaw = raw?.orgLicense;
        const orgLicense: OrgLicense | undefined = orgLicenseRaw
            ? {
                active: !!orgLicenseRaw.active,
                type: orgLicenseRaw.type ?? null,
                expiresAt: orgLicenseRaw.expiresAt ?? null,
                orgName: orgLicenseRaw.orgName ?? null
            }
            : undefined;

        return {
            plan: raw?.plan ?? { key: 'plus', term: 'monthly', status: 'none', active: false, cancelAtPeriodEnd: false, currentPeriodStart: null, currentPeriodEnd: null, accessExpiresAt: null },
            credits: raw?.credits ?? { submission: 0, custom_case_package: 0 },
            canPurchaseAddons: raw?.canPurchaseAddons ?? false,
            hasUsedTrial: raw?.hasUsedTrial ?? false,
            orgLicense
        };
    }
}
